import React, { useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform, ScrollView } from 'react-native';
import { openExternal } from '../src/utils/openExternal';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../src/context/AuthContext';
import { useTheme } from '../src/context/ThemeContext';
import { useToast } from '../src/components/Toast';
import type { ColorTheme } from '../src/utils/theme';
import Button from '../src/components/Button';
import ContentContainer from '../src/components/ContentContainer';
import { spacing, fontSize, fontWeight } from '../src/utils/theme';
import { rs } from '../src/utils/responsive';

/** WelcomeScreen - Landing page with branding, feature highlights, and auth navigation. */
export default function WelcomeScreen() {
  const { isAuthenticated, isLoading, role } = useAuth();
  const router = useRouter();
  const { colors } = useTheme();
  const toast = useToast();
  const params = useLocalSearchParams<{ expired?: string }>();
  const styles = useStyles(colors);

  // Show session expired toast
  useEffect(() => {
    if (params.expired === '1') {
      toast.show({ type: 'warning', title: 'Session expired', message: 'Please sign in again.' });
      // Clear param from URL
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [params.expired]);

  // Redirect authenticated users to their role-specific dashboard
  useEffect(() => {
    if (!isLoading && isAuthenticated && role) {
      if (role === 'admin') {
        router.replace('/admin');
      } else if (role === 'owner') {
        router.replace('/owner/dashboard');
      } else if (role === 'user') {
        router.replace('/user');
      }
    }
  }, [isLoading, isAuthenticated, role]);

  const openOwnerSite = () => {
    openExternal('https://sales.rinsepay.app');
  };

  if (isLoading) {
    return (
      <View style={styles.container}>
        <Ionicons name="water" size={64} color={colors.primary} />
        <Text style={styles.loadingText}>RinsePay</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
        <ContentContainer maxWidth={480}>
          <View style={styles.hero}>
          <View style={styles.iconContainer}>
            <Ionicons name="water" size={80} color={colors.white} />
          </View>
          {/* Brand logotype: "Rinse" teal + "Pay" brand brown (#733e24 in
              light theme; the dark theme uses its contrast-safe brown). */}
          <Text style={styles.title}>
            <Text style={{ color: colors.primary }}>Rinse</Text>
            <Text style={{ color: colors.secondary }}>Pay</Text>
          </Text>
          <Text style={styles.subtitle}>The modern way to pay for car washes</Text>
        </View>

        <View style={styles.features}>
          <FeatureItem step={1} text="Scan the QR code located on any paystation" styles={styles} />
          <FeatureItem step={2} text="Pay securely with any card, Google or Apple Pay" styles={styles} />
          <FeatureItem step={3} text="Wash and rate your experience or report any issues" styles={styles} />
        </View>

        <View style={styles.actions}>
          <Button title="Sign In" onPress={() => router.push('/login')} size="lg" style={styles.button} />
          <Button title="Create Account" onPress={() => router.push('/register')} variant="outline" size="lg" style={styles.button} />
        </View>

        <View style={styles.ownerLink}>
          <Ionicons name="business" size={18} color={colors.primary} />
          <Text style={styles.ownerLinkText}>
            Own a car wash? See how RinsePay can increase your revenue.{' '}
            <Text style={styles.ownerLinkCta} onPress={openOwnerSite} accessibilityRole="link">Click Here</Text>
          </Text>
        </View>

        <TouchableOpacity onPress={() => router.push('/terms')} style={styles.tosLink}>
          <Text style={styles.tosText}>Terms of Service</Text>
        </TouchableOpacity>
        </ContentContainer>
      </ScrollView>
    </View>
  );
}

/** FeatureItem - numbered step row ("1." / "2." / "3.") with description. */
interface FeatureItemProps {
  readonly step: number;
  readonly text: string;
  readonly styles: ReturnType<typeof useStyles>;
}

function FeatureItem({ step, text, styles }: FeatureItemProps) {
  return (
    <View style={styles.featureItem}>
      <View style={styles.featureIcon}>
        <Text style={styles.featureStep}>{step}</Text>
      </View>
      <Text style={styles.featureText}>{text}</Text>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  scroll: { flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.lg },
  hero: { alignItems: 'center', marginBottom: spacing.xxl },
  iconContainer: {
    width: rs(140), height: rs(140), borderRadius: rs(70),
    backgroundColor: c.primary,
    justifyContent: 'center', alignItems: 'center',
    marginBottom: spacing.lg,
    shadowColor: c.primary, shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.3, shadowRadius: 16, elevation: 8,
  },
  title: { fontSize: fontSize.hero, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.sm },
  subtitle: { fontSize: fontSize.lg, color: c.textSecondary, textAlign: 'center' },
  features: { width: '100%', marginBottom: spacing.xxl },
  featureItem: { flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.sm },
  featureIcon: {
    width: rs(44), height: rs(44), borderRadius: rs(22),
    backgroundColor: c.primaryBg,
    justifyContent: 'center', alignItems: 'center', marginRight: spacing.md,
  },
  featureStep: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.primary },
  featureText: { flex: 1, fontSize: fontSize.md, color: c.text, fontWeight: fontWeight.medium },
  actions: { width: '100%' },
  button: { marginBottom: spacing.md },
  ownerLink: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    marginTop: spacing.lg, paddingVertical: spacing.sm, paddingHorizontal: spacing.md,
    borderRadius: rs(12), borderWidth: 1, borderColor: c.primaryBg, backgroundColor: c.primaryBg,
  },
  ownerLinkText: { fontSize: fontSize.sm, color: c.primary, fontWeight: fontWeight.medium, marginLeft: spacing.xs, flexShrink: 1 },
  ownerLinkCta: { fontSize: fontSize.sm, color: c.primary, fontWeight: fontWeight.bold, textDecorationLine: 'underline' },
  tosLink: { alignItems: 'center', marginTop: spacing.sm },
  tosText: { fontSize: fontSize.xs, color: c.textLight },
  loadingText: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.primary, marginTop: spacing.md },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
